/* eslint-disable sonarjs/cognitive-complexity */

import { useState } from 'react';

import dynamic from 'next/dynamic';
import Image from 'next/image';

import cupIcon from '@/assets/icons/pet-profile-cup-icon.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import Button from '@/components/V2/Button';
import cn from '@/helpers/utils/classNames';
import { isPuptimusPrime } from '@/helpers/utils/customer';
import { useAppContext } from '@/layouts/AppProvider';
import { PetImageObj } from '@/store/slices/petProfile';
import { PetIssue, PetProfileType } from '@/types/PetProfile';

import Spinner from '../../Spinner';
import { ThreeDotsDropdownProps } from '../ThreeDotsDropdown';
import UploadProfile from '../UploadProfile';

const Tooltip = dynamic(() => import('@/components/V2/Tooltip'));
const ThreeDotsDropdown = dynamic(() => import('../ThreeDotsDropdown'));

interface PetProfileCardProps {
  onEditClick: () => void;
  onDeleteClick: (profile: PetProfileType) => void;
  onRecommendedClick?: (profileId: number) => void;
  profile: PetProfileType;
  baseUrl: string;
  onProfilePictureChange: (updatedPetImage: PetImageObj | undefined, profileId: number) => void;
  isPetLink?: boolean;
  isLoading?: boolean;
  recommendedId?: number;
}

const PetProfileCard = ({
  onDeleteClick,
  onRecommendedClick,
  onEditClick,
  profile,
  baseUrl,
  onProfilePictureChange,
  onEditMouseEnter,
  isPetLink = false,
  isLoading = false,
  recommendedId,
}: PetProfileCardProps & Pick<ThreeDotsDropdownProps, 'onEditMouseEnter'>) => {
  const { customer } = useAuthContext();
  const { isBackOffice } = useAppContext();

  const primaryIssues = profile.petIssues?.filter((issue: PetIssue) => issue.petIssueMainType === 'Primary') || [];
  const otherIssues = profile.petIssues?.filter((issue: PetIssue) => issue.petIssueMainType === 'Secondary') || [];

  const updatedPetAllergies = profile.isDogProfile
    ? profile.petAllergies
    : profile.petAllergies.filter(allergy => allergy !== 'Beef');

  // Food Allergies
  const firstFoodAllergy = updatedPetAllergies?.[0] || 'N/A';
  const foodAllergiesTooltip = updatedPetAllergies.slice(1).join(', ');
  const remainingFoodAllergyCount = updatedPetAllergies?.length - 1;

  // Pet Diets
  const firstPetDiet = profile.petDailyDiet?.[0] || 'No Diet';
  const petDietsTooltip = profile.petDailyDiet.slice(1).join(', ');
  const remainingPetDietCount = profile.petDailyDiet?.length - 1;

  // Primary Issues
  const firstPrimaryIssue = primaryIssues?.[0]?.name || 'No Primary Issue';
  const primaryIssuesTooltip = primaryIssues
    .slice(1)
    .map(issue => issue.name)
    .join(', ');
  const remainingPrimaryIssueCount = primaryIssues?.length - 1;

  // Other Issues
  const firstOtherIssue = otherIssues?.[0]?.name || 'No Other Issue';
  const otherIssuesTooltip = otherIssues
    .slice(1)
    .map(issue => issue.name)
    .join(', ');
  const remainingOtherIssueCount = otherIssues?.length - 1;

  const imageUrl = profile?.image ? `${baseUrl}${profile.image?.replace(/\\/g, '/')}` : '';
  const croppedImageUrl = profile?.croppedImage ? `${baseUrl}${profile.croppedImage?.replace(/\\/g, '/')}` : '';

  const [isOpen, setOpen] = useState(false);
  const toggleList = () => setOpen(!isOpen);

  const hideDeleteBtn = isBackOffice && customer && isPuptimusPrime(customer);

  return (
    <div className="">
      <div
        className={cn(
          'v2-p-4 v2-flex v2-gap-2 v2-items-center v2-justify-between v2-bg-white v2-rounded-xs v2-mb-4 v2-cursor-pointer',
          { '!v2-mb-0 v2-pb-0': isPetLink }
        )}
        onClick={toggleList}
        onMouseEnter={() => onEditMouseEnter && onEditMouseEnter()}
      >
        <div className="v2-flex v2-gap-[10px] v2-items-center">
          <UploadProfile
            onProfilePictureChange={onProfilePictureChange}
            isDog={profile.isDogProfile}
            imageUrl={imageUrl}
            croppedImageUrl={croppedImageUrl}
            proportion={profile.proportion}
            profileId={profile.petProfileId}
          />
          <div>
            <p className="v2-font-sweet-sans-pro-medium v2-text-base v2-text-[#101828]">{profile.name}</p>
            <div className="v2-flex v2-gap-1 v2-items-center">
              <Image src={cupIcon} alt="cup icon" />
              <span className="v2-font-sweet-sans-pro v2-text-xs v2-text-[#344054]">
                {!profile?.cupsPerDay ? <Spinner /> : profile?.cupsPerDay + ' Cups'}
              </span>
            </div>
          </div>
        </div>
        {!isPetLink && (
          <div className="v2-py-2" onClick={evt => evt.stopPropagation()}>
            <ThreeDotsDropdown
              profile={profile}
              onDeleteClick={onDeleteClick}
              onEditClick={onEditClick}
              onEditMouseEnter={onEditMouseEnter}
            />
          </div>
        )}
      </div>
      <div
        className={cn(
          'v2-grid v2-grid-cols-2 v2-gap-x-2 v2-gap-y-3 v2-bg-white v2-rounded-xs v2-p-4 lg:v2-grid md:v2-grid',
          { 'sm:v2-hidden xs:v2-hidden': !isOpen },
          { 'sm:v2-grid xs:v2-grid': isOpen }
        )}
      >
        <ColumnCard label="Age" value={profile.petAgeID === 1 ? '<3 months' : profile.petAge} doNotTruncate={true} />
        <ColumnCard label="Birthday" value={profile.dob?.split('T')?.[0] || ''} />
        <ColumnCard label="Gender" value={profile.gender} />
        <ColumnCard label="Size" value={profile.breedSize} />
        <ColumnCard label="Breed" value={profile.breedName} />

        {/* Food Allergy */}
        <ColumnCard
          label="Food Allergy"
          value={firstFoodAllergy}
          count={remainingFoodAllergyCount > 0 ? `+${remainingFoodAllergyCount}` : undefined}
          tooltip={foodAllergiesTooltip}
          isSingleElement={updatedPetAllergies?.length === 1}
        />

        {/* Primary Issues */}
        <ColumnCard
          label="Primary Issues"
          value={firstPrimaryIssue}
          count={remainingPrimaryIssueCount > 0 ? `+${remainingPrimaryIssueCount}` : undefined}
          tooltip={primaryIssuesTooltip}
          isSingleElement={primaryIssues?.length === 1}
        />

        {/* Other Issues */}
        <ColumnCard
          label="Other Issues"
          value={firstOtherIssue}
          count={remainingOtherIssueCount > 0 ? `+${remainingOtherIssueCount}` : undefined}
          tooltip={otherIssuesTooltip}
          isSingleElement={otherIssues?.length === 1}
        />

        {/* Pet Diets */}
        <ColumnCard
          label="Pet Diets"
          value={firstPetDiet}
          count={remainingPetDietCount > 0 ? `+${remainingPetDietCount}` : undefined}
          tooltip={petDietsTooltip}
          isSingleElement={profile.petDailyDiet?.length === 1}
        />

        <ColumnCard label="Weight Category" value={profile.weightCategory} />
        <ColumnCard label="Weight" value={profile.currentWeight} />
        <ColumnCard label="Shares Food With" value={profile.shareFoodWith || 'None'} />
      </div>
      {isPetLink && (
        <div className="v2-grid v2-gap-[12px] v2-px-[16px] v2-pt-[4px] v2-pb-[16px] v2-bg-white">
          <Button
            as="button"
            type="button"
            btnSize="md"
            variant="filled"
            scheme="primary"
            className="v2-w-full v2-h-[44px] v2-flex v2-items-center v2-justify-center"
            onClick={() => onRecommendedClick && onRecommendedClick(profile.petProfileId)}
            disabled={recommendedId === profile.petProfileId && isLoading}
          >
            Recommended Products{' '}
            {recommendedId === profile.petProfileId && isLoading && <Spinner className="v2-ml-2" />}
          </Button>
          {hideDeleteBtn && customer && (
            <Button
              as="button"
              type="button"
              btnSize="md"
              variant="outlined"
              scheme="danger"
              className="v2-w-full v2-h-[44px] v2-flex v2-justify-center v2-border-[#FDA29B]"
              onClick={() => onDeleteClick(profile)}
              disabled={isBackOffice && customer === undefined}
            >
              Delete Profile
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PetProfileCard;

const ColumnCard = ({
  label,
  value,
  tooltip,
  count,
  isSingleElement = false,
  doNotTruncate = false,
}: {
  label: string;
  value: string;
  count?: string;
  tooltip?: React.ReactNode;
  isSingleElement?: boolean;
  doNotTruncate?: boolean;
}) => {
  const MAX_CHAR_LIMIT = 12; // Constant truncation length
  const isLongName = value?.length > MAX_CHAR_LIMIT;
  const truncatedValue = !doNotTruncate && isLongName ? `${value.slice(0, MAX_CHAR_LIMIT)}...` : value;

  return (
    <div className="v2-w-full">
      <p className="v2-font-sweet-sans-pro v2-text-[#667085] v2-text-sm v2-mb-1">{label}</p>
      <p className="v2-font-sweet-sans-pro-medium v2-text-sm v2-text-[#101828] v2-flex">
        {/* Tooltip for Truncated or Remaining Items */}
        {isLongName ? (
          <Tooltip
            title={
              <div className="v2-font-sweet-sans-pro v2-font-medium v2-text-[12px] v2-leading-[18px] v2-text-center">
                {value}
              </div>
            }
            placement="top"
            variant="primary"
          >
            <span>{truncatedValue}</span>
          </Tooltip>
        ) : (
          <span>{value}</span>
        )}

        {/* Additional Elements Tooltip */}
        {tooltip && count && (
          <Tooltip
            title={
              <div className="v2-font-sweet-sans-pro v2-font-medium v2-text-[12px] v2-leading-[18px] v2-text-[#101828] v2-text-center">
                {tooltip}
              </div>
            }
            placement="top"
            variant="light"
          >
            <span className="v2-underline v2-font-sweet-sans-pro v2-text-sm v2-text-primary-700 v2-ml-1">{count}</span>
          </Tooltip>
        )}
      </p>
    </div>
  );
};
